/**
 * Sticky action footer for the classification side panel — Cancel / Classify
 * with an optional `classifyHint` line above the primary CTA.
 *
 * ADR-014 / EFR-002: the always-visible secondary off-ramp button (the old
 * "Dismiss") is now OPTIONAL — the panel no longer renders it, because the
 * single non-reporting resolution ("Exclude from report") is reached via the
 * Exclude type-toggle option / triage tile like any other classification, not
 * via a permanent escape hatch. The optional `onDismiss` is retained so the
 * shared footer can still host a secondary action if a future panel needs one.
 */
import { cn } from '@/lib/utils';

export type ClassifyHint = { type: 'neutral' | 'gate'; messages: string[] };

interface ActionFooterProps {
  isEditing: boolean;
  canClassify: boolean;
  isClassifying: boolean;
  isDismissing?: boolean;
  blockedByGates: boolean;
  classifyHint: ClassifyHint | null;
  classifyLabel?: string;
  dismissLabel?: string;
  onCancel: () => void;
  onClassify: () => void;
  onDismiss?: () => void;
}

export function ActionFooter({
  isEditing,
  canClassify,
  isClassifying,
  isDismissing,
  blockedByGates,
  classifyHint,
  classifyLabel,
  dismissLabel,
  onCancel,
  onClassify,
  onDismiss,
}: ActionFooterProps) {
  return (
    <div
      data-testid="panel-footer"
      className="sticky bottom-0 border-t bg-background pt-3 mt-3"
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Cancel
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClassify}
            disabled={!canClassify || isClassifying || blockedByGates}
            aria-describedby={classifyHint ? 'classify-hint' : undefined}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-medium transition-colors',
              canClassify && !isClassifying && !blockedByGates
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-muted text-muted-foreground cursor-not-allowed',
            )}
          >
            {isEditing ? 'Save Changes' : (classifyLabel ?? 'Classify')}
          </button>
          {onDismiss && (
            <>
              <div className="h-6 w-px bg-border" />
              <button
                type="button"
                onClick={onDismiss}
                disabled={isDismissing}
                className="rounded-md border border-destructive/30 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
              >
                {dismissLabel ?? 'Dismiss'}
              </button>
            </>
          )}
        </div>
      </div>
      {classifyHint && (
        <div
          id="classify-hint"
          data-testid="classify-hint"
          aria-live="polite"
          className={cn(
            'mt-2 text-xs',
            classifyHint.type === 'gate' ? 'text-red-600' : 'text-muted-foreground',
          )}
        >
          {classifyHint.messages.length === 1 ? (
            <p>{classifyHint.messages[0]}</p>
          ) : (
            <ul className="list-disc pl-4 space-y-0.5">
              {classifyHint.messages.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
